import axios from "axios";
import numberToWords from "../utils/numberToWords";
import { renderDate } from "../utils/dateFormatter";

// Cache for company details
let companyDetailsCache = null;

export default async function generateInvoice(invoiceId, type, mode = "preview", outputType = "pdf") {
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  const cdnUrl = process.env.REACT_APP_CDN_URL || "";
  const companyid = localStorage.getItem("companyid");
  const companyLogo = localStorage.getItem("companylogo");
  const companyName = localStorage.getItem("company");
  const logoNetShine = companyLogo && companyLogo.trim() ? `${cdnUrl}/${companyLogo}` : 'https://netshine-assets.blr1.digitaloceanspaces.com/company_87/logos/company_logo_87_1739613280498';
  const defaultLogoSrc = companyLogo && companyLogo.trim() ? `${cdnUrl}/${companyLogo}` : logoNetShine;

  // Default settings
  let settingsData = {
    logo: { enabled: true, file: null },
    footer: { enabled: true, text: "" },
    header: { enabled: true, text: "" },
    showLogo: false,
    showHeaderSeparator: false,
    headerLineHeight: "120px",
    template: "default",
    exclusions: ["room-service"],
    invoiceCaption: "Hotel Invoice",
    lineItems: 15,
    showChargeCode: false,
    chargeCodeHeading: "",
  };

  // Default invoice data structure
  let invoiceData = {
    invoiceInfo: { number: "INV-001", date: "March 03, 2025" },
    roomDetails: [{ folioResNo: "N/A", guestName: "N/A", checkIn: "N/A", checkOut: "N/A", roomNo: "N/A", billTo: "N/A", billToAddress: "N/A", billToGSTIN: "N/A", showGSTDetails: false }],
    billingSummary: [],
    taxDetails: { subtotal: "0.00", taxBreakdown: [], grandTotal: "0.00", totalTax: "0.00" },
    amountInWords: "Zero Rupees Only",
    billingDetails: { name: "N/A" },
    advancePayments: [],
  };

  // Fetch charge codes from the API
  let chargeCodeMap = {};
  try {
    console.log("Making API call to fetch charge codes...");
    const chargeCodesResponse = await axios.get(`${apiUrl}/extra-charges-codes/listings`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    console.log("Charge codes response:", chargeCodesResponse);
    if (chargeCodesResponse.data) {
      Object.values(chargeCodesResponse.data).forEach(charge => {
        chargeCodeMap[charge.charge_type] = charge.charge_code !== null ? charge.charge_code.toString() : "";
      });
    }
    console.log("Charge Code Map:", chargeCodeMap);
    
  } catch (error) {
    console.error("Error fetching charge codes:", error);
    if (error.response) {
      console.error("Response status:", error.response.status);
      console.error("Response data:", error.response.data);
    }
  }

  console.log("After fetching charge codes...");

  // Fetch company details and settings concurrently
  let companyData = companyDetailsCache;
  let noSettingsFound = false;

  if (!companyData) {
    try {
      const [companyResponse, settingsResponse] = await Promise.all([
        fetch(`${apiUrl}/company/company-details/${companyid}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }),
        axios.get(`${apiUrl}/setting-hms/`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { companyid },
        }),
      ]);

      if (!companyResponse.ok) throw new Error("Failed to fetch company details");
      companyData = await companyResponse.json();
      companyDetailsCache = companyData;
      console.log("Company API Response:", companyData);

      if (settingsResponse.data.success && settingsResponse.data.settings?.invoice_settings) {
        settingsData = { ...settingsData, ...settingsResponse.data.settings.invoice_settings };
        settingsData.headerLineHeight = `${settingsData.headerLineHeight}px`;
      } else {
        noSettingsFound = true;
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      companyData = {
        company: "Company Name",
        address: "Address",
        city: "City",
        phone: "N/A",
        email: "N/A",
        country: "N/A",
        gst_no: "N/A",
      };
      companyDetailsCache = companyData;
      noSettingsFound = true;
    }
  } else {

    try {
      const settingsResponse = await axios.get(`${apiUrl}/setting-hms/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { companyid },
      });
      if (settingsResponse.data.success && settingsResponse.data.settings?.invoice_settings) {
        settingsData = { ...settingsData, ...settingsResponse.data.settings.invoice_settings };
        settingsData.headerLineHeight = `${settingsData.headerLineHeight}px`;
      } else {
        noSettingsFound = true;
      }
    } catch (error) {
      console.error("Error fetching invoice settings:", error);
      noSettingsFound = true;
    }
  }
  // If no settings are found, set dynamic default header and footer
  if (noSettingsFound) {
    const defaultHeader = `
      <h1 style="font-size: 16px; margin: 0;">${companyData.company || "Company Name"}</h1>
      <p style="line-height: 12px; margin: 2px 0;">
        ${companyData.address || "Address"}, ${companyData.city || "City"}<br>
        Phone: ${companyData.phone || "N/A"}<br>
        Email: ${companyData.email || "N/A"}<br>
        Country: ${companyData.country || "N/A"}<br>
        GST No: ${companyData.gst_no || "N/A"}
      </p>
    `;
    settingsData.header.text = defaultHeader;
    settingsData.header.enabled = true;

    const defaultFooter = `
      <p style="font-size: 10px; margin-top: 5px; margin-bottom: 2px;"><strong>Declaration:</strong></p>
      <p style="font-size: 10px; margin: 2px 0;">We declare that this invoice shows the actual price of the services described and that all particulars are true and correct.</p>
      <div style="display: flex; justify-content: space-between; padding-top: 1px; font-size: 12px; margin: 2px 0;">
        <p style="margin: 0;">Guest Signature: _________________________</p>
        <p style="text-align: right; margin: 0;">For ${companyData.company || "Company Name"}<br>Authorised Signatory</p>
      </div>
      <p style="font-size: 9px; color: #666; margin-top: 5px; text-align: center;">Thank you for your stay!<br>For inquiries, contact us at info@hotelsunshine.com</p>
    `;
    settingsData.footer.text = defaultFooter;
    settingsData.footer.enabled = true;
  }

  // Transform footer HTML to preserve Quill editor alignment classes
  if (settingsData.footer.enabled && settingsData.footer.text) {
    let footerText = settingsData.footer.text;
    // Replace Quill alignment classes with inline styles
footerText = footerText
  .replace(/class="ql-align-center"/g, 'style="text-align: center;"')
  .replace(/class="ql-align-right"/g, 'style="text-align: right;"')
  .replace(/class="ql-align-left"/g, 'style="text-align: left;"');

settingsData.footer.text = `
  <div style="width: 100%; margin-top: 5px; font-size: 12px;">
    <div style="display: flex; justify-content: flex-end;">
      <div style="width: 30%; text-align: right;">
        <div><strong>Authorised Signatory</strong></div>
        <div style="margin-top: 20px;">${companyName}</div>
      </div>
    </div>
    <div style="margin-top: -25px;">${footerText}</div>
  </div>`;


  }
  // Common function to format dates
  const formatDate = (date) => new Date(date).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  const formatShortDate = (date) => new Date(date).toLocaleDateString("en-US", { day: "2-digit", month: "2-digit", year: "2-digit" });

  // Fetch and process invoice/proforma data
  if (type === "invoice" && invoiceId) {
    try {
      const invoiceResponse = await axios.get(`${apiUrl}/transactions/invoice`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { invoiceId },
      });
      if (invoiceResponse.data.success && invoiceResponse.data.invoice) {
        const invoice = invoiceResponse.data.invoice;
        const sortedTransactions = invoice.transactions.sort((a, b) => {
          if (a.transaction_id !== b.transaction_id) {
            return a.transaction_id - b.transaction_id;
          }
          return new Date(a.charge_date) - new Date(b.charge_date);
        });

        const subtotal = sortedTransactions.reduce((sum, item) => sum + Number(item.amount), 0);
        const totalTax = invoice.tax_breakdown ? invoice.tax_breakdown.reduce((sum, tax) => sum + Number(tax.taxAmount), 0) : 0;

        const hasGSTDetails = invoice.gst_number || invoice.gst_company_name;

        
        invoiceData = {
          invoiceInfo: {
            number: invoice.display_invoice_id,
            date: renderDate(invoice.invoice_date),
            guest_email: invoice.guest_email,
          },
          roomDetails: [{
            folioResNo: invoice.booking_number || "N/A",
            guestName: invoice.guest_name || "N/A",
            checkIn: renderDate(invoice.checkin_date || invoice.created_at),
            checkOut: renderDate(invoice.checkout_date || invoice.created_at),
            roomNo: invoice.room_numbers || "N/A",
            billTo: hasGSTDetails ? (invoice.gst_company_name) : "",
            billToAddress: hasGSTDetails ? (invoice.gst_company_address || "") : "",
            billToGSTIN: hasGSTDetails ? (invoice.gst_number || "") : "",
            showGSTDetails: hasGSTDetails,
            guestEmail: invoice.guest_email,
          }],
          billingSummary: sortedTransactions.map((item) => {
            console.log(`Mapping charge_type: ${item.charge_type} to chargeCode: ${chargeCodeMap[item.charge_type]}`);
            return {
              date: renderDate(item.charge_date),
              description: item.description,
              chargeType: item.charge_type,
              chargeCode: chargeCodeMap[item.charge_type] || (item.charge_type === "ROOM_RENT" ? "" : ""),
              charges: Number(item.amount).toFixed(2),
              taxAmount: Number(item.tax_amount).toFixed(2),
              totalAmount: Number(item.total_amount).toFixed(2),
            };
          }),
          taxDetails: {
            subtotal: subtotal.toFixed(2),
            taxBreakdown: invoice.tax_breakdown || [],
            totalTax: totalTax.toFixed(2),
            grandTotal: Number(invoice.balance_due).toFixed(2),
          },

          amountInWords: numberToWords(Number(invoice.net_amount)),
          billingDetails: { name: invoice.billed_to || "" },
          advancePayments: invoice.payment_details.map((item) => ({
            paymentDate: formatShortDate(item.chargeDate),
            description: `${item.paymentMode} Payment`,
            amount: Number(item.totalAmount).toFixed(2),
          })),
        };

        
      }
    } catch (error) {
      console.error("Error fetching invoice details:", error);
    }
  } else if (type === "proforma" && invoiceId) {
    try {
      const response = await axios.get(`${apiUrl}/transactions/guest-folio`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { bookingId: invoiceId.bookingId },
      });
      if (response.data.success) {
        const folio = response.data.folio || {};
        const booking = folio.booking || {};
        const guest = folio.guest || {};
        const roomNumbers = [...new Set(folio.roomRent?.map(item => {
          const match = item.reference_type?.match(/Room charge for room no\. (\S+)/);
          return match ? match[1] : "N/A";
        }) || [])].join(", ") || "N/A";

        const subtotal = ((folio.totals?.totalRoomRent || 0) + (folio.totals?.totalAdditionalCharges || 0));
        const totalTax = (folio.totals?.totalTax || 0);
        const totalAdvance = (folio.totals?.totalPayments || 0);
        const grandTotal = (Number(subtotal) + totalTax - totalAdvance);

        const allEntries = [
          ...(folio.roomRent || []).map(item => ({ ...item, type: 'roomRent' })),
          ...(folio.additionalCharges || []).map(item => ({ ...item, type: 'additionalCharge' })),
        ].sort((a, b) => {
          if (a.charge_id !== b.charge_id) {
            return (a.charge_id || 0) - (b.charge_id || 0);
          }
          return new Date(a.charge_date) - new Date(b.charge_date);
        });

        const proformaSubtotal = allEntries.reduce((sum, item) => sum + Number(item.amount), 0);
        const taxBreakdown = folio.roomRent?.[0]?.tax_breakdown || folio.additionalCharges?.[0]?.tax_breakdown || [];
        const calculatedTotalTax = taxBreakdown.length > 0 ? taxBreakdown.reduce((sum, tax) => sum + Number(tax.taxAmount), 0) : totalTax;

        const hasGSTDetails = guest.gst_number || guest.gst_company_name;

        invoiceData = {
          invoiceInfo: {
            number: `Proforma-${booking.booking_number || "UNKNOWN"}`,
            date: renderDate(new Date()),
          },
          roomDetails: [{
            folioResNo: booking.booking_number || "N/A",
            guestName: guest.full_name || "N/A",
            guestEmail: guest.email || "",
            checkIn: renderDate(booking.check_in_date),
            checkOut: renderDate(booking.check_out_date),
            roomNo: roomNumbers,
            billTo: hasGSTDetails ? (guest.gst_company_name) : "",
            billToAddress: hasGSTDetails ? (guest.gst_company_address || "") : "",
            billToGSTIN: hasGSTDetails ? (guest.gst_number || "") : "",
            showGSTDetails: hasGSTDetails,
          }],
          billingSummary: allEntries.map(item => {
            const charges = Number(item.amount);
            const taxAmount = Number(item.tax_amount || 0);
            const chargeType = item.type === 'roomRent' ? "ROOM_RENT" : (item.charge_type === "ADDITIONAL_CHARGES" ? "ADDITIONAL_CHARGES" : item.charge_type);
            console.log(`Mapping charge_type: ${chargeType} to chargeCode: ${chargeCodeMap[chargeType]}`);
            return {
              date: renderDate(item.charge_date),
              description: item.reference_type,
              chargeType: chargeType,
              chargeCode: chargeCodeMap[chargeType] || (item.type === 'roomRent' ? "" : ""),
              charges: charges.toFixed(2),
              taxAmount: taxAmount.toFixed(2),
              totalAmount: (charges + taxAmount).toFixed(2),
            };
          }),
          taxDetails: {
            subtotal: proformaSubtotal.toFixed(2),
            taxBreakdown: taxBreakdown,
            totalTax: calculatedTotalTax.toFixed(2),
            grandTotal: grandTotal.toFixed(2),
          },
          amountInWords: numberToWords(Number(grandTotal)),
          
          billingDetails: { name: guest.gst_number ? "" : "" },

          advancePayments: (folio.payments || []).map(item => ({
            paymentDate: formatShortDate(item.charge_date),
            description: `${item.payment_type || "N/A"} Payment`,
            amount: Number(item.amount).toFixed(2),
          })),
        };
      }
    } catch (error) {
      console.error("Error fetching proforma folio data:", error);
    }
  }

  const logoSrc = settingsData.showLogo ? (settingsData.logo.file ? `${cdnUrl}/${settingsData.logo.file}` : defaultLogoSrc) : "";
  const BLANK_LINE = { date: " ", description: " ", chargeCode: " ", charges: " ", taxAmount: " ", totalAmount: " " };

  const generateTaxBreakdown = (taxBreakdown, totalTax) => {
    if (!taxBreakdown || taxBreakdown.length === 0 || totalTax === "0.00") return '';

    return `
      <div style="width: 100%; margin-bottom: 3px;">
        <div style="display: flex; width: 100%; padding: 6px 8px; background-color: #f8f8f8; border-radius: 4px; font-size: 12px; box-sizing: border-box; font-weight: bold;">
          <div style="width: 33.33%; padding: 2px 4px; box-sizing: border-box;">Tax Name</div>
          <div style="width: 33.33%; padding: 2px 4px; box-sizing: border-box; text-align: center;">Rate</div>
          <div style="width: 33.33%; padding: 2px 4px; box-sizing: border-box; text-align: right;">Amount</div>
        </div>
        <hr style="border: none; border-top: 1px solid #ccc; margin: 0;"/>
        ${taxBreakdown.map((tax, index) => `
          <div style="display: flex; width: 100%; padding: 4px 8px; font-size: 12px; box-sizing: border-box; align-items: center;">
            <div style="width: 33.33%; padding: 2px 4px;">${tax.taxName}</div>
            <div style="width: 33.33%; padding: 2px 4px; text-align: center;">${tax.taxRate}%</div>
            <div style="width: 33.33%; padding: 2px 4px; text-align: right;">${Number(tax.taxAmount).toFixed(2)}</div>
          </div>
          ${index < taxBreakdown.length - 1 ? '<hr style="border: none; border-top: 1px solid #eee; margin: 0 8px;" />' : ''}
        `).join('')}
        <hr style="border: none; border-top: 1px solid #ccc; margin: 0;"/>
        <div style="display: flex; width: 100%; padding: 6px 8px; box-sizing: border-box; font-size: 12px; align-items: center;">
          <div style="width: 66.66%; padding: 2px 4px; text-align: right;"><strong>Total:</strong></div>
          <div style="width: 33.33%; padding: 2px 4px; text-align: right;">₹ ${totalTax}</div>
        </div>
        <hr style="border: none; border-top: 1px solid #ccc; margin: 0 8px;" />
      </div>
    `;
  };

  const generateAdvancePayments = (advancePayments) => {
    if (!advancePayments || advancePayments.length === 0) return '';

    return `
      <div style="width: 100%; margin-bottom: 3px;">
        <div style="display: flex; width: 100%; padding: 3px 0; border-bottom: 1px solid #ccc; border-top: 1px solid #ccc;font-weight: bold;">
          <div style="width: 33.33%; padding-left: 3px;">Payment Date</div>
          <div style="width: 33.33%; padding-left: 3px;">Description</div>
          <div style="width: 33.33%; padding-right: 3px; text-align: right;">Amount</div>
        </div>
        <hr style="border: none; border-top: 1px solid #ccc; margin: 0;"/>
        ${advancePayments.map((item, index) => `
          <div style="display: flex; width: 100%; padding: 3px 0; border-bottom: 1px solid #eee;">
            <div style="width: 33.33%; padding-left: 3px;">${item.paymentDate}</div>
            <div style="width: 33.33%; padding-left: 3px;">${item.description}</div>
            <div style="width: 33.33%; padding-right: 3px; text-align: right;">${item.amount}</div>
          </div>
        `).join('')}
        <hr style="border: none; border-top: 1px solid #ccc; margin: 0;"/>
        <div style="display: flex; width: 100%; padding: 6px 0 0; border-top: 1px solid #ccc; font-weight: bold;">
          <div style="width: 66.66%; text-align: right; padding-right: 4px;">Total:</div>
          <div style="width: 33.33%; text-align: right; padding-right: 4px;">
            ${advancePayments.reduce((sum, item) => sum + Number(item.amount), 0).toFixed(2)}
          </div>
        </div>
        <hr style="border: none; border-top: 1px solid #ccc; margin: 0 8px;" />
      </div>
    `;
  };

  const invoiceHTML = `
  <!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${type === "proforma" ? "Proforma Invoice" : settingsData.invoiceCaption}</title>
  <style>
    @page { size: A4; margin: 1mm; }
    body { font-family: Arial, sans-serif; font-size: 12px; color: #333; margin: 0; padding: 0; }
  </style>
</head>
<body>
  <div style="width: 100%; max-width: 200mm; margin: 0 auto; padding: 1px; box-sizing: border-box;">
    ${settingsData.header.enabled ? `
      <div style="min-height: ${settingsData.headerLineHeight}; height: ${settingsData.headerLineHeight}; margin-bottom: 10px; overflow: hidden;">
        <div style="display: flex; justify-content: space-between; align-items: flex-start;">
          <div style="width: 40%;">
            ${settingsData.showLogo ? `<img src="${logoSrc}" alt="Logo" style="max-width: 180px; max-height: ${settingsData.headerLineHeight}; height: auto;">` : ""}
          </div>
          <div style="width: 60%; text-align: right; line-height: 2px;">
            ${settingsData.header.text}
          </div>
        </div>
        ${settingsData.showHeaderSeparator ? `
          <hr style="border: 1px; height: 0.5px; background: #ccc; margin: 0;" />
        ` : ""}
      </div>
    ` : ""}
    
    <h1 style="text-align: center; font-size: 24px; font-weight: bold; margin: 10px 0;margin-bottom:40px;">${type === "proforma" ? "PROFORMA INVOICE" : settingsData.invoiceCaption.toUpperCase()}</h1>
    <hr style="border: 1px; height: 1px; background: #ccc; margin: 0;"/>

    <div style="display: flex; justify-content: space-between; margin-bottom: 10px;">
      <div style="width: 48%;">
        <p style="line-height: 16px; margin: 2px 0;"><strong>Folio No. / Res No:</strong> ${invoiceData.roomDetails[0].folioResNo}</p>
        <p style="line-height: 16px; margin: 2px 0;"><strong>Invoice No:</strong> ${invoiceData.invoiceInfo.number}</p>
        <p style="line-height: 16px; margin: 2px 0;"><strong>Check-in Date:</strong> ${invoiceData.roomDetails[0].checkIn}</p>
        <p style="line-height: 16px; margin: 2px 0;"><strong>Room No:</strong> ${invoiceData.roomDetails[0].roomNo}</p>
      </div>
      <div style="width: 48%;">
        <p style="line-height: 16px; margin: 2px 0;"><strong>Invoice Date:</strong> ${invoiceData.invoiceInfo.date}</p>
        ${invoiceData.billingDetails.name ? `
        ` : ''}
                <p style="line-height: 16px; margin: 2px 0;"><strong>Guest Name:</strong> ${invoiceData.roomDetails[0].guestName}</p>

        <p style="line-height: 16px; margin: 2px 0;"><strong>Check-out Date:</strong> ${invoiceData.roomDetails[0].checkOut}</p>
        ${invoiceData.roomDetails[0].billTo ? `
          <p style="line-height: 16px; margin: 2px 0;"><strong>Bill To:</strong> ${invoiceData.roomDetails[0].billTo}</p>
          ${invoiceData.roomDetails[0].showGSTDetails && invoiceData.roomDetails[0].billToAddress ? `
            <p style="line-height: 16px; margin: 2px 0;"><strong>Bill To Address:</strong> ${invoiceData.roomDetails[0].billToAddress.replace(/\n/g, '<br />')}</p>
          ` : ''}
          ${invoiceData.roomDetails[0].showGSTDetails && invoiceData.roomDetails[0].billToGSTIN ? `
            <p style="line-height: 16px; margin: 2px 0;"><strong>Bill To GSTIN:</strong> ${invoiceData.roomDetails[0].billToGSTIN}</p>
          ` : ''}
        ` : ''}
      </div>
    </div>
    <hr style="border: 1px; height: 1px; background: #ccc; margin: 0;"/>

    <div style="width: 100%; margin-bottom: 3px;">
      <div style="display: flex; font-weight: 500px; border-bottom: 1px solid #333;border-top: 1px solid #333; padding: 6px 0; background-color: #f9f9f9;">
        <div style="width: ${settingsData.showChargeCode ? "14%" : "16%"}; padding-left: 8px; box-sizing: border-box;">Date</div>
        <div style="width: ${settingsData.showChargeCode ? "40%" : "45%"}; padding-left: 8px; box-sizing: border-box;">Description</div>
        ${settingsData.showChargeCode ? `
          <div style="width: 10%; padding-left: 8px; box-sizing: border-box;">${settingsData.chargeCodeHeading || "Charge Code"}</div>
        ` : ""}
        <div style="width: ${settingsData.showChargeCode ? "15%" : "14%"}; padding-right: 8px; box-sizing: border-box; text-align: right;">Charges</div>
        <div style="width: ${settingsData.showChargeCode ? "12%" : "14%"}; padding-right: 8px; box-sizing: border-box; text-align: right;">Tax Amount</div>
        <div style="width: ${settingsData.showChargeCode ? "12%" : "14%"}; padding-right: 8px; box-sizing: border-box; text-align: right;">Total Amount </div>
      </div>
      <hr style="border: 1px; height: 0.5px; background: #ccc; margin: 0;"/>
      ${(() => {
    const lines = invoiceData.billingSummary;
    const hasBillToGSTIN = invoiceData.roomDetails[0]?.billToGSTIN && invoiceData.roomDetails[0].billToGSTIN.trim() !== "";
    const paddingLines = hasBillToGSTIN ? Math.max(0, settingsData.lineItems - lines.length - 5) : Math.max(0, settingsData.lineItems - lines.length);
    const paddedLines = [...lines, ...Array(paddingLines).fill(BLANK_LINE)].slice(0, settingsData.lineItems);

  

        return paddedLines.map((item, index) => `
          <div style="display: flex; width: 100%; padding: 6px 0; ${!item.date.trim() && !item.description.trim() && !item.chargeCode?.trim() ? 'background-color: #FFFFFF;' : ''}">
            <div style="width: ${settingsData.showChargeCode ? "14%" : "16%"}; padding-left: 8px; box-sizing: border-box; line-height: 1.4;">${item.date || "--"}</div>
            <div style="width: ${settingsData.showChargeCode ? "40%" : "50%"}; padding-left: 8px; box-sizing: border-box; line-height: 1.4;">${item.description || "--"}</div>
            ${settingsData.showChargeCode ? `
              <div style="width: 10%; padding-left: 8px; box-sizing: border-box; line-height: 1.4;">${item.chargeCode || ""}</div>
            ` : ""}
            <div style="width: ${settingsData.showChargeCode ? "12%" : "14%"}; padding-right: 8px; box-sizing: border-box; text-align: right; line-height: 1.4;">${item.charges || ""}</div>
            <div style="width: ${settingsData.showChargeCode ? "12%" : "14%"}; padding-right: 8px; box-sizing: border-box; text-align: right; line-height: 1.4;">${item.taxAmount || ""}</div>
            <div style="width: ${settingsData.showChargeCode ? "12%" : "14%"}; padding-right: 8px; box-sizing: border-box; text-align: right; line-height: 1.4;">${item.totalAmount || ""}</div>
          </div>
          ${index < paddedLines.length - 1 && (item.date.trim() || item.description.trim() || item.chargeCode?.trim()) ? '<hr style="border: 1px; height: 0.5px; background: #ccc; margin: 0;"/>' : ''}
          ${index === paddedLines.length - 1 ? '<hr style="border: 1px; height: 0.5px; background: #ccc; margin: 0;"/>' : ''}
        `).join("");
      })()}
    </div>

    <div style="display: flex; justify-content: space-between;">
      <div style="width: 55%;">
        ${invoiceData.taxDetails.taxBreakdown.length > 0 && invoiceData.taxDetails.totalTax !== "0.00" ? `
          ${generateTaxBreakdown(invoiceData.taxDetails.taxBreakdown, invoiceData.taxDetails.totalTax)}
        ` : ''}
        ${invoiceData.advancePayments.length > 0 ? `
          ${generateAdvancePayments(invoiceData.advancePayments)}
        ` : ''}
        <p style="padding: 2px 0; font-size: 12px; font-weight: bold; margin: 10px 0 0 0;">
          <strong>Amount in Words:</strong> ${invoiceData.amountInWords}
        </p>
      </div>
      <div style="width: 40%;">
        <div style="width: 100%; margin-top: 8px; padding: 8px 12px; background-color: #fdfdfd; border-radius: 4px; font-size: 12px; box-sizing: border-box;">
          ${invoiceData.taxDetails.taxBreakdown.length > 0 && invoiceData.taxDetails.totalTax !== "0.00" ? `
            <div style="display: flex; justify-content: space-between; padding: 4px 0;">
              <div style="width: 50%; text-align: right;"><strong>Subtotal:</strong></div>
              <div style="width: 50%; text-align: right;">₹ ${invoiceData.taxDetails.subtotal}</div>
            </div>
            <hr style="border: none; border-top: 1px solid #ccc; margin: 4px 0;"/>
            <div style="display: flex; justify-content: space-between; padding: 4px 0;">
              <div style="width: 50%; text-align: right;"><strong>Tax:</strong></div>
              <div style="width: 50%; text-align: right;">₹ ${invoiceData.taxDetails.totalTax}</div>
            </div>
            <hr style="border: none; border-top: 1px dashed #ccc; margin: 4px 0;"/>
            <div style="display: flex; justify-content: space-between; padding: 4px 0;">
              <div style="width: 50%; text-align: right;"><strong>Total:</strong></div>
              <div style="width: 50%; text-align: right;">₹ ${(Number(invoiceData.taxDetails.subtotal) + Number(invoiceData.taxDetails.totalTax)).toFixed(2)}</div>
            </div>
            <hr style="border: none; border-top: 1px solid #ccc; margin: 4px 0;"/>
          ` : `
            <div style="display: flex; justify-content: space-between; padding: 4px 0;">
              <div style="width: 50%; text-align: right;"><strong>Total:</strong></div>
              <div style="width: 50%; text-align: right;">₹ ${(Number(invoiceData.taxDetails.subtotal) + Number(invoiceData.taxDetails.totalTax)).toFixed(2)}</div>
            </div>
            <hr style="border: none; border-top: 1px solid #ccc; margin: 4px 0;"/>
          `}
          ${invoiceData.advancePayments.length > 0 ? `
            <div style="display: flex; justify-content: space-between; padding: 4px 0;">
              <div style="width: 50%; text-align: right;"><strong>Advance Received:</strong></div>
              <div style="width: 50%; text-align: right;">₹ ${invoiceData.advancePayments.reduce((sum, item) => sum + Number(item.amount), 0).toFixed(2)}</div>
            </div>
            <hr style="border: none; border-top: 1px dashed #ccc; margin: 4px 0;"/>
          ` : ''}
          <div style="display: flex; justify-content: space-between; padding: 6px 0;">
            <div style="width: 50%; text-align: right; font-weight: bold;"><strong>Amount Payable:</strong></div>
            <div style="width: 50%; text-align: right; font-weight: bold;">₹ ${invoiceData.taxDetails.grandTotal}</div>
          </div>
          <hr style="border: none; border-top: 1px solid #ccc; margin: 4px 0;"/>
        </div>
      </div>
    </div>

    ${settingsData.footer.enabled ? `
      <div style="margin-top: 5px;">
        ${settingsData.footer.text}
      </div>
    ` : ""}
  </div>
</body>
</html>`;

  // Return based on outputType
  if (outputType === "html") {
    return invoiceHTML; // Return the HTML string directly
  }

  // Otherwise, proceed with PDF generation
  try {
    if (mode === "email") {
      const response = await axios.post(`${apiUrl}/transactions/send-invoice-email`, [
        {
          bookingId: invoiceId.bookingId,
          guestName: invoiceData.roomDetails[0].guestName,
          guestEmail: invoiceData.roomDetails[0].guestEmail,
          checkInDate: invoiceData.roomDetails[0].checkIn,
          checkOutDate: invoiceData.roomDetails[0].checkOut,
          invoiceNo: invoiceData.invoiceInfo.number,
          amount: invoiceData.taxDetails.grandTotal,
          html: invoiceHTML,
        },
      ], {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data; // Return JSON response
    } else {
      const response = await axios.post(`${apiUrl}/transactions/generate-pdf`, { html: invoiceHTML }, {
        responseType: "arraybuffer",
      });

      const pdfBlob = new Blob([response.data], { type: "application/pdf" });
      return pdfBlob;
    }
  } catch (error) {
    console.error("Error generating PDF from server:", error);
    throw new Error("Failed to generate PDF");
  }
}